/**
 * Chart of Accounts Page (Settings)
 * List, create, and edit the tenant's general ledger accounts.
 *
 * Accounts cannot be deleted: once part of the ledger they are permanent
 * record (there is no delete endpoint). After any journal line is posted to an
 * account, its number, type and normal balance are locked by the backend; the
 * form greys those fields out using the account's `hasJournalLines` flag.
 *
 * Only ADMIN users may add/edit accounts or seed the default chart (backend
 * requireRole('ADMIN')); other roles get a read-only table.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createAPIClient, IGLAccountsApi } from '../../api';
import * as types from '../../api/types';
import { getErrorMessage } from '../../utils/apiError';
import { canManageGLAccounts, canViewLedger } from '../../utils/permissions';
import { ledgerListPath } from '../ledgerUi';
import {
  ACCOUNT_NAME_MAX_LENGTH,
  ACCOUNT_NUMBER_MAX_LENGTH,
  EMPTY_GL_ACCOUNT_FORM,
  GLAccountFormState,
  GL_ACCOUNT_TYPE_OPTIONS,
  NORMAL_BALANCE_OPTIONS,
  buildCreateRequest,
  buildUpdateRequest,
  defaultNormalBalance,
  formFromAccount,
  formatEnumLabel,
  getParentOptions,
  sortByAccountNumber,
  validateGLAccountForm,
} from '../../utils/glAccount';
import {
  inputClass,
  labelClass,
  primaryButtonClass,
  secondaryButtonClass,
  rowActionClass,
} from '../../components/formStyles';
import { Toast } from '../../components/Toast';

type Editing = { mode: 'create' } | { mode: 'edit'; account: types.GLAccount } | null;

const thClass = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

export function ChartOfAccountsPage() {
  const { user } = useAuth();
  const apiClient = React.useMemo<IGLAccountsApi>(() => createAPIClient(), []);
  const canManage = canManageGLAccounts(user);
  // Journal reads are office-only on the backend; Settings itself is open to every role.
  const showActivity = canViewLedger(user);
  const hasActions = canManage || showActivity;

  // State
  const [accounts, setAccounts] = useState<types.GLAccount[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSeeding, setIsSeeding] = useState(false);

  const [editing, setEditing] = useState<Editing>(null);
  const [form, setForm] = useState<GLAccountFormState>(EMPTY_GL_ACCOUNT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const refreshAccounts = useCallback(async () => {
    const list = await apiClient.listGLAccounts();
    setAccounts(sortByAccountNumber(list));
  }, [apiClient]);

  // Load data
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        await refreshAccounts();
      } catch (err) {
        setError(getErrorMessage(err, 'Failed to load chart of accounts'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [refreshAccounts]);

  const accountsById = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);

  const editingId = editing?.mode === 'edit' ? editing.account.id : null;
  const parentOptions = useMemo(() => getParentOptions(accounts, editingId), [accounts, editingId]);
  const isLocked = editing?.mode === 'edit' && editing.account.hasJournalLines;

  const setField = <K extends keyof GLAccountFormState>(field: K, value: GLAccountFormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccountTypeChange = (accountType: types.GLAccountType) => {
    // Suggest the conventional normal balance for the type; still overridable.
    setForm((prev) => ({ ...prev, accountType, normalBalance: defaultNormalBalance(accountType) }));
  };

  const openCreate = () => {
    setEditing({ mode: 'create' });
    setForm(EMPTY_GL_ACCOUNT_FORM);
    setFormError(null);
  };

  const openEdit = (account: types.GLAccount) => {
    setEditing({ mode: 'edit', account });
    setForm(formFromAccount(account));
    setFormError(null);
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY_GL_ACCOUNT_FORM);
    setFormError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;

    const validationError = validateGLAccountForm(form, accounts, editingId);
    if (validationError) {
      setFormError(validationError);
      return;
    }

    try {
      setIsSaving(true);
      setFormError(null);

      if (editing.mode === 'edit') {
        const request = buildUpdateRequest(form, editing.account);
        if (Object.keys(request).length > 0) {
          await apiClient.updateGLAccount(editing.account.id, request);
        }
      } else {
        await apiClient.createGLAccount(buildCreateRequest(form));
      }

      closeForm();
      await refreshAccounts();
    } catch (err) {
      // e.g. 400 "Cannot change account type of account 1200 because it has posted journal entries..."
      setFormError(
        getErrorMessage(err, editing.mode === 'edit' ? 'Failed to update account' : 'Failed to create account')
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSeed = async () => {
    try {
      setIsSeeding(true);
      setError(null);
      await apiClient.seedChartOfAccounts();
      await refreshAccounts();
    } catch (err) {
      setError(getErrorMessage(err, 'Failed to seed chart of accounts'));
    } finally {
      setIsSeeding(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Title */}
      <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6">
        <div className="min-w-0">
          <Link to="/settings" className="text-sm font-semibold text-gray-500 hover:text-blue-600">
            ← Settings
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 mt-1 break-words">Chart of Accounts</h1>
        </div>
        {canManage && !editing && !isLoading && accounts.length > 0 && (
          <button onClick={openCreate} className={`${primaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`}>
            Add Account
          </button>
        )}
      </div>

      {error && <Toast kind="error">{error}</Toast>}

      {/* Add / Edit Form */}
      {canManage && editing && (
        <section className="bg-white rounded-lg shadow mb-6">
          <form onSubmit={handleSubmit} noValidate>
            <div className="p-4 sm:p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4 break-words">
                {editing.mode === 'edit'
                  ? `Edit ${editing.account.accountNumber} · ${editing.account.accountName}`
                  : 'New Account'}
              </h2>

              {formError && <Toast kind="error">{formError}</Toast>}

              {isLocked && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-md">
                  <p className="text-amber-800 text-sm">
                    <span className="font-semibold">Locked:</span> this account has posted journal entries, so its
                    account number, type and normal balance can no longer change. Name, postable, and parent account
                    can still be edited.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                <div className="sm:col-span-2">
                  <label htmlFor="glAccountNumber" className={labelClass}>
                    Account number<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <input
                    id="glAccountNumber"
                    type="text"
                    value={form.accountNumber}
                    onChange={(e) => setField('accountNumber', e.target.value)}
                    disabled={isSaving || isLocked}
                    maxLength={ACCOUNT_NUMBER_MAX_LENGTH}
                    placeholder="1400"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-4">
                  <label htmlFor="glAccountName" className={labelClass}>
                    Account name<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <input
                    id="glAccountName"
                    type="text"
                    value={form.accountName}
                    onChange={(e) => setField('accountName', e.target.value)}
                    disabled={isSaving}
                    maxLength={ACCOUNT_NAME_MAX_LENGTH}
                    placeholder="Prepaid Expenses"
                    className={inputClass}
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="glAccountType" className={labelClass}>
                    Account type<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <select
                    id="glAccountType"
                    value={form.accountType}
                    onChange={(e) => handleAccountTypeChange(e.target.value as types.GLAccountType)}
                    disabled={isSaving || isLocked}
                    className={inputClass}
                  >
                    {GL_ACCOUNT_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>
                        {formatEnumLabel(type)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="glNormalBalance" className={labelClass}>
                    Normal balance<span className="ml-0.5 text-red-600">*</span>
                  </label>
                  <select
                    id="glNormalBalance"
                    value={form.normalBalance}
                    onChange={(e) => setField('normalBalance', e.target.value as GLAccountFormState['normalBalance'])}
                    disabled={isSaving || isLocked}
                    className={inputClass}
                  >
                    {NORMAL_BALANCE_OPTIONS.map((balance) => (
                      <option key={balance} value={balance}>
                        {formatEnumLabel(balance)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="glParent" className={labelClass}>
                    Parent account
                  </label>
                  <select
                    id="glParent"
                    value={form.parentId}
                    onChange={(e) => setField('parentId', e.target.value)}
                    disabled={isSaving}
                    className={inputClass}
                  >
                    <option value="">None</option>
                    {parentOptions.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.accountNumber} · {account.accountName}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="sm:col-span-6">
                  <label htmlFor="glIsPostable" className="inline-flex items-start sm:items-center gap-2 text-sm text-gray-700 min-h-[44px] sm:min-h-0">
                    <input
                      id="glIsPostable"
                      type="checkbox"
                      checked={form.isPostable}
                      onChange={(e) => setField('isPostable', e.target.checked)}
                      disabled={isSaving}
                      className="mt-0.5 sm:mt-0 h-5 w-5 sm:h-4 sm:w-4 flex-none rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>
                      <span className="font-medium">Postable</span>
                      <span className="text-gray-500"> — journal entries can be posted directly to this account</span>
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 sm:px-6 py-4">
              <button type="button" onClick={closeForm} disabled={isSaving} className={`${secondaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`}>
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className={`${primaryButtonClass} w-full sm:w-auto min-h-[44px] sm:min-h-0`}>
                {isSaving ? 'Saving...' : editing.mode === 'edit' ? 'Save Changes' : 'Create Account'}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="bg-white rounded-lg shadow p-4 sm:p-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && accounts.length === 0 && (
          <div className="text-center py-12">
            <h2 className="text-lg font-semibold text-gray-900">No accounts yet</h2>
            {canManage ? (
              <>
                <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">
                  Start with the default chart of accounts (cash, receivables, sales tax payable, revenue, cost of
                  goods sold, and more). You can rename accounts or add your own afterwards.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
                  <button onClick={handleSeed} disabled={isSeeding} className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`}>
                    {isSeeding ? 'Seeding...' : 'Seed default chart of accounts'}
                  </button>
                  {!editing && (
                    <button onClick={openCreate} disabled={isSeeding} className={`${secondaryButtonClass} min-h-[44px] sm:min-h-0`}>
                      Add account manually
                    </button>
                  )}
                </div>
              </>
            ) : (
              <p className="mt-2 text-sm text-gray-600">An administrator needs to set up the chart of accounts.</p>
            )}
          </div>
        )}

        {/* Table */}
        {!isLoading && accounts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="responsive-table min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={thClass}>Number</th>
                  <th className={thClass}>Name</th>
                  <th className={thClass}>Type</th>
                  <th className={thClass}>Normal Balance</th>
                  <th className={thClass}>Postable</th>
                  <th className={thClass}>Parent</th>
                  {hasActions && (
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <span className="sr-only">Actions</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {accounts.map((account) => {
                  const parent = account.parentId ? accountsById.get(account.parentId) : undefined;
                  return (
                    <tr key={account.id}>
                      <td data-label="Number" data-primary className="px-4 py-3 font-mono text-sm font-semibold text-gray-900">
                        {account.accountNumber}
                      </td>
                      <td data-label="Name" className="px-4 py-3 text-sm text-gray-900 break-words">
                        <span className={account.parentId ? 'pl-4' : undefined}>{account.accountName}</span>
                        {account.hasJournalLines && (
                          <span
                            className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600"
                            title="Has posted journal entries: number, type and normal balance are locked"
                          >
                            In use
                          </span>
                        )}
                      </td>
                      <td data-label="Type" className="px-4 py-3 text-sm text-gray-600">{formatEnumLabel(account.accountType)}</td>
                      <td data-label="Normal Balance" className="px-4 py-3 text-sm text-gray-600">{formatEnumLabel(account.normalBalance)}</td>
                      <td data-label="Postable" className="px-4 py-3 text-sm">
                        {account.isPostable ? (
                          <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                            Yes
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                            No
                          </span>
                        )}
                      </td>
                      <td data-label="Parent" className="px-4 py-3 text-sm text-gray-600 break-words">
                        {account.parentId
                          ? parent
                            ? `${parent.accountNumber} · ${parent.accountName}`
                            : 'Unknown'
                          : '—'}
                      </td>
                      {hasActions && (
                        <td className="px-4 py-3 text-right md:whitespace-nowrap flex flex-wrap gap-x-4 gap-y-2 md:table-cell md:space-x-4">
                          {showActivity && (
                            <Link
                              to={ledgerListPath({ accountId: account.id })}
                              aria-label={`View activity for ${account.accountNumber} ${account.accountName}`}
                              className={rowActionClass}
                            >
                              View activity
                            </Link>
                          )}
                          {canManage && (
                            <button
                              onClick={() => openEdit(account)}
                              disabled={isSaving}
                              aria-label={`Edit ${account.accountNumber} ${account.accountName}`}
                              className={rowActionClass}
                            >
                              Edit
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
}
